import { CardFace, CardFaceData } from '../../global-types';
import { getPermanentObjects } from './get-permanent-objects';
import { getSumCardFaceObjects } from './get-sum-card-face-objects';

/**
 * Classification of the reconciliation between a rendered card face
 * and the template it was originally generated from.
 */
export enum FaceValidationStatus {
  /** Permanent object count on canvas meets or exceeds the template requirement. */
  Valid = 'valid',
  /**
   * Template has more required objects than the current canvas.
   * This is a recoverable state (e.g. project was saved against an older template
   * version). Callers should not treat this as fatal.
   */
  TemplateEvolved = 'template-evolved',
}

/**
 * Structured outcome describing how a card face compares against its template.
 *
 * Contract for callers observing a `TemplateEvolved` status: intentionally log
 * the divergence to Datadog RUM (via `datadogRum.addAction`) for observability
 * and continue the user flow. Do not throw or trigger a system-failure dialog
 * on this status.
 */
export type FaceValidationResult = {
  faceId: number;
  status: FaceValidationStatus;
  templateCount: number;
  permanentCount: number;
  /** Positive when the template requires more objects than are present on the canvas. */
  delta: number;
};

/**
 * Compute a structured validation result for a single face without throwing.
 *
 * @param cardFace - the current card face data (canvas ref + metadata)
 * @param templateCardFace - the template face this canvas was generated from
 * @returns structured result classifying the face state and reporting counts/delta
 */
export const getFaceValidationResult = (cardFace: CardFaceData, templateCardFace: CardFace): FaceValidationResult => {
  const templateCount = getSumCardFaceObjects(templateCardFace);
  const canvasObjects = cardFace.canvas.current?.getObjects() ?? [];
  const permanentCount = getPermanentObjects(canvasObjects).length;
  const delta = templateCount - permanentCount;

  return {
    faceId: cardFace.faceId,
    status: delta > 0 ? FaceValidationStatus.TemplateEvolved : FaceValidationStatus.Valid,
    templateCount,
    permanentCount,
    delta,
  };
};
